"use client";

import type { RefObject } from "react";
import { PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import {
  InlineComboboxFooter,
  InlineComboboxList,
} from "./inline-combobox-list";
import { minSearchLengthMessage } from "./inline-combobox-options";
import type {
  InlineComboboxController,
  ResolvedInlineComboboxProps,
} from "./use-inline-combobox";

const POPOVER_WIDTH_CLASSES =
  "w-[var(--radix-popper-anchor-width)] min-w-[min(16rem,calc(100vw-2rem))] p-0";

interface InlineComboboxPopoverProps {
  props: ResolvedInlineComboboxProps;
  combobox: InlineComboboxController;
}

function InlineComboboxListbox({
  props,
  combobox,
}: InlineComboboxPopoverProps) {
  const { onEditOption } = props;
  return (
    <div
      id={combobox.listId}
      role="listbox"
      className="max-h-[min(18rem,calc(var(--radix-popover-content-available-height,18rem)-0.5rem))] overflow-y-auto py-1"
    >
      <InlineComboboxList
        listId={combobox.listId}
        groups={combobox.filteredGroups}
        value={props.value}
        activeValue={combobox.activeValue}
        isLoading={props.isLoading}
        emptyText={props.emptyText}
        minSearchMessage={minSearchLengthMessage(
          Boolean(props.onSearchChange),
          props.minSearchLength,
          combobox.query,
        )}
        optionIcon={props.optionIcon}
        renderOption={props.renderOption}
        onEditOption={
          onEditOption
            ? (optionValue) => {
                combobox.setOpen(false);
                onEditOption(optionValue);
              }
            : undefined
        }
        editAsText={props.variant === "card"}
        onChoose={combobox.choose}
        onHighlight={combobox.setHighlightedValue}
      />
    </div>
  );
}

export function InlineComboboxPopover({
  props,
  combobox,
  anchorRef,
}: InlineComboboxPopoverProps & {
  anchorRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <PopoverContent
      className={cn(POPOVER_WIDTH_CLASSES, props.popoverClassName)}
      align="start"
      collisionPadding={8}
      side="bottom"
      avoidCollisions={!props.openBelow}
      onOpenAutoFocus={(event) => event.preventDefault()}
      onInteractOutside={(event) => {
        if (
          event.target instanceof Node &&
          anchorRef.current?.contains(event.target)
        ) {
          event.preventDefault();
        }
      }}
    >
      <InlineComboboxListbox props={props} combobox={combobox} />
      {props.notice && !props.isLoading ? (
        <p className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
          {props.notice}
        </p>
      ) : null}
      <InlineComboboxFooter
        action={props.action}
        footer={props.footer}
        query={combobox.query}
        close={() => combobox.setOpen(false)}
      />
    </PopoverContent>
  );
}
